import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Waves, Fish, Ship, Anchor, Wind, AlertTriangle, Sun,
  Droplets, Navigation, ArrowLeft, Compass, Eye, Gauge,
  Thermometer, CloudRain, Loader2, ChevronRight, Timer, Activity
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FALLBACK_LOCATION = { lat: 35.2098048, lon: -86.0794285 };

function degreesToCardinal(deg) {
  if (deg == null) return "N/A";
  const dirs = ["N","NNE","NE","ENE","E","ESE","SE","SSE","S","SSW","SW","WSW","W","WNW","NW","NNW"];
  return dirs[Math.floor((deg + 11.25) / 22.5) % 16];
}

function DirectionArrow({ deg, className = "w-4 h-4" }) {
  if (deg == null) return null;
  return (
    <Navigation
      className={className}
      style={{ transform: `rotate(${deg}deg)`, transition: "transform 0.3s" }}
    />
  );
}

function RatingBadge({ rating, isDarkMode }) {
  const colors = {
    "Excellent": "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    "Very Good": "bg-green-500/20 text-green-400 border-green-500/30",
    "Good": "bg-lime-500/20 text-lime-400 border-lime-500/30",
    "Fair": "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    "Poor": "bg-orange-500/20 text-orange-400 border-orange-500/30",
    "Dangerous": "bg-red-500/20 text-red-400 border-red-500/30",
  };
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${colors[rating] || colors["Fair"]}`}>
      {rating}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, sub, color = "text-blue-400", isDarkMode }) {
  return (
    <div data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`} className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-gray-50 border border-gray-100'}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className={`text-xs ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>{label}</span>
      </div>
      <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{value}</p>
      {sub && <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>{sub}</p>}
    </div>
  );
}

function MarineSourceOverlay({ mode, isDarkMode }) {
  const text = mode === "inland"
    ? "USGS water stations + OpenWeatherMap lake-weather context"
    : "NOAA marine signals + OpenWeatherMap live conditions";
  return (
    <div className={`mb-6 rounded-xl border px-4 py-3 text-sm ${isDarkMode ? 'bg-blue-500/10 border-blue-400/20 text-blue-200' : 'bg-blue-50 border-blue-200 text-blue-800'}`} data-testid="marine-page-source-comparison-badge">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-semibold">Marine source overlay</span>
        <span className="text-xs opacity-70" data-testid="marine-page-source-comparison-note">{text}</span>
      </div>
    </div>
  );
}

function HourlyForecastRow({ entry, isDarkMode }) {
  const time = new Date(entry.time).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const waveH = entry.wave_height_ft != null ? `${entry.wave_height_ft}ft` : "-";
  const swellH = entry.swell_height_ft != null ? `${entry.swell_height_ft}ft` : "-";
  const wDir = entry.wave_direction != null ? degreesToCardinal(entry.wave_direction) : "-";
  const curr = entry.current_velocity_knots != null ? `${entry.current_velocity_knots}kt` : "-";

  return (
    <div className={`grid grid-cols-5 text-xs py-2 px-3 ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'} transition-colors`}>
      <span className={`font-medium ${isDarkMode ? 'text-white/70' : 'text-gray-600'}`}>{time}</span>
      <span className={`text-center ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{waveH}</span>
      <span className={`text-center ${isDarkMode ? 'text-white/70' : 'text-gray-600'}`}>{swellH}</span>
      <span className={`text-center ${isDarkMode ? 'text-white/60' : 'text-gray-500'}`}>{wDir}</span>
      <span className={`text-center ${isDarkMode ? 'text-white/60' : 'text-gray-500'}`}>{curr}</span>
    </div>
  );
}

export default function MarineForecastPage() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const [searchParams] = useSearchParams();
  const defaultLocation = user?.preferences?.default_location || FALLBACK_LOCATION;
  const lat = parseFloat(searchParams.get("lat") || defaultLocation.lat || FALLBACK_LOCATION.lat);
  const lon = parseFloat(searchParams.get("lon") || defaultLocation.lon || FALLBACK_LOCATION.lon);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllHours, setShowAllHours] = useState(false);
  const [isPremium, setIsPremium] = useState(null);

  useEffect(() => {
    const checkSubscription = async () => {
      try {
        const res = await axios.get(`${API}/subscription/status`);
        setIsPremium(res.data?.is_premium || res.data?.is_trial || user?.role === "admin");
      } catch {
        setIsPremium(user?.role === "admin");
      }
    };
    if (user) checkSubscription();
  }, [user]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/weather/marine/detailed`, { params: { lat, lon } });
      setData(resp.data);
      setError(null);
    } catch (err) {
      setError("Failed to load marine forecast");
    } finally {
      setLoading(false);
    }
  }, [lat, lon]);

  useEffect(() => {
    fetchData();
  }, [lat, lon, fetchData]);

  if (isPremium === null) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-[#0a0e1a]' : 'bg-gray-50'}`}>
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  if (!isPremium) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-[#0a0e1a] text-white' : 'bg-gray-50 text-gray-900'}`}>
        <div className="max-w-4xl mx-auto p-6">
          <Link to="/" data-testid="marine-premium-back-link" className={`flex items-center gap-2 mb-8 ${isDarkMode ? 'text-white/60 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}>
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </Link>
          <div className="text-center py-20">
            <Anchor className="w-16 h-16 mx-auto mb-4 text-blue-400 opacity-50" />
            <h2 className="text-2xl font-bold mb-2">Premium Feature</h2>
            <p className={`${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>
              Marine Forecast is available for premium subscribers.
            </p>
            <Link to="/subscription" data-testid="marine-upgrade-link" className="inline-block mt-4 px-6 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-500 transition-colors">
              Upgrade to Premium
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-[#0a0e1a]' : 'bg-gray-50'}`}>
        <Loader2 className={`w-8 h-8 animate-spin ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
      </div>
    );
  }

  if (!data?.success || (!data?.is_coastal && !data?.is_inland_water)) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-[#0a0e1a] text-white' : 'bg-gray-50 text-gray-900'}`}>
        <div className="max-w-4xl mx-auto p-6">
          <Link to="/" data-testid="marine-empty-back-link" className={`flex items-center gap-2 mb-8 ${isDarkMode ? 'text-white/60 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}>
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </Link>
          <div className="text-center py-20">
            <Waves className="w-16 h-16 mx-auto mb-4 text-blue-400 opacity-40" />
            <h2 className="text-xl font-bold mb-2">No Water Data Available</h2>
            <p className={`${isDarkMode ? 'text-white/50' : 'text-gray-500'} max-w-md mx-auto`}>
              {data?.message || "Unable to load water conditions for this location."}
            </p>
            <Link to="/" data-testid="marine-empty-return-link" className={`inline-flex items-center gap-1 mt-4 text-blue-400 hover:text-blue-300`}>
              <ArrowLeft className="w-4 h-4" /> Return to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ===== INLAND WATER / LAKE VIEW =====
  if (data?.is_inland_water) {
    const { current_conditions: lc, weather: wx, water_monitoring: wm, conditions: cond } = data;
    const usgsStations = wm?.stations || [];

    return (
      <div data-testid="marine-forecast-page" className={`min-h-screen pb-12 ${isDarkMode ? 'bg-[#0a0e1a] text-white' : 'bg-gray-50 text-gray-900'}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <Link to="/" data-testid="marine-back-btn" className={`flex items-center gap-2 text-sm ${isDarkMode ? 'text-white/60 hover:text-white' : 'text-gray-500 hover:text-gray-900'} transition-colors`}>
              <ArrowLeft className="w-4 h-4" /> Dashboard
            </Link>
            <button onClick={fetchData} data-testid="marine-inland-refresh-button" className={`text-xs px-3 py-1.5 rounded-lg ${isDarkMode ? 'bg-white/10 hover:bg-white/15 text-white/70' : 'bg-gray-200 hover:bg-gray-300 text-gray-600'} transition-colors`}>
              Refresh
            </button>
          </div>

          {/* Title */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className={`p-2.5 rounded-xl ${isDarkMode ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                <Anchor className={`w-6 h-6 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold">Lake & Water Forecast</h1>
                <p className={`text-sm ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>
                  Inland water conditions for boating, fishing & recreation
                </p>
              </div>
            </div>
            {cond?.boating?.advisory && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-3 flex items-center gap-2 bg-yellow-500/15 text-yellow-400 border border-yellow-500/25 text-sm font-medium px-4 py-2.5 rounded-xl"
              >
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                {cond.boating.advisory}
              </motion.div>
            )}
          </div>

          <MarineSourceOverlay mode="inland" isDarkMode={isDarkMode} />

          {/* Current Lake Conditions */}
          <section className="mb-8">
            <h2 className={`text-sm font-semibold uppercase tracking-wider mb-4 ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>
              Current Lake Conditions
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              <StatCard icon={Waves} label="Water Surface" value={lc.lake_conditions} sub={`Est. waves ~${lc.estimated_wave_inches}" high`} color="text-blue-400" isDarkMode={isDarkMode} />
              <StatCard icon={Wind} label="Wind" value={`${lc.wind_speed_mph} mph`} sub={`${lc.wind_direction}${lc.wind_gust_mph ? ` · Gusts ${lc.wind_gust_mph} mph` : ''}`} color="text-slate-400" isDarkMode={isDarkMode} />
              <StatCard icon={Eye} label="Visibility" value={`${lc.visibility_mi} mi`} sub={lc.description} color="text-amber-400" isDarkMode={isDarkMode} />
              <StatCard icon={Thermometer} label="Air Temp" value={`${lc.air_temp_f}°F`} sub={`Humidity ${lc.humidity}%`} color="text-red-400" isDarkMode={isDarkMode} />
              {wx?.water_temp_f && (
                <StatCard icon={Thermometer} label="Water Temp" value={`${wx.water_temp_f}°F`} sub="USGS monitoring" color="text-cyan-400" isDarkMode={isDarkMode} />
              )}
              <StatCard icon={Gauge} label="Pressure" value={`${lc.pressure_hpa} hPa`} sub={lc.pressure_hpa > 1015 ? "High pressure" : lc.pressure_hpa < 1005 ? "Low pressure" : "Normal"} color="text-purple-400" isDarkMode={isDarkMode} />
              <StatCard icon={Sun} label="UV Index" value={lc.uv_index} sub={lc.uv_index >= 8 ? "Very High" : lc.uv_index >= 6 ? "High" : lc.uv_index >= 3 ? "Moderate" : "Low"} color="text-yellow-400" isDarkMode={isDarkMode} />
            </div>
          </section>

          {/* Boating & Fishing Conditions */}
          <section className="mb-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Boating */}
            <div className={`p-5 rounded-2xl ${isDarkMode ? 'bg-white/5 border border-white/5' : 'bg-white border border-gray-200'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Anchor className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                  <h3 className="font-semibold">Boating Conditions</h3>
                </div>
                <div className={`px-3 py-1 rounded-full text-sm font-bold ${
                  cond.boating.index >= 8 ? 'bg-green-500/20 text-green-400' :
                  cond.boating.index >= 5 ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-red-500/20 text-red-400'
                }`}>
                  {cond.boating.index}/10 {cond.boating.rating}
                </div>
              </div>
              <div className="w-full bg-gray-700/30 rounded-full h-2.5 mb-3">
                <div
                  className={`h-2.5 rounded-full ${
                    cond.boating.index >= 8 ? 'bg-green-400' :
                    cond.boating.index >= 5 ? 'bg-yellow-400' : 'bg-red-400'
                  }`}
                  style={{ width: `${cond.boating.index * 10}%` }}
                />
              </div>
              {cond.boating.warnings?.length > 0 && (
                <ul className={`text-xs space-y-1 ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>
                  {cond.boating.warnings.map((w, i) => (
                    <li key={`warn-${w}`} className="flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3 text-yellow-400" /> {w}
                    </li>
                  ))}
                </ul>
              )}
              {(!cond.boating.warnings || cond.boating.warnings.length === 0) && (
                <p className={`text-xs ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>
                  Good conditions for boating, kayaking, and paddleboarding.
                </p>
              )}
            </div>

            {/* Fishing */}
            <div className={`p-5 rounded-2xl ${isDarkMode ? 'bg-white/5 border border-white/5' : 'bg-white border border-gray-200'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Activity className={`w-5 h-5 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
                  <h3 className="font-semibold">Fishing Conditions</h3>
                </div>
                <div className={`px-3 py-1 rounded-full text-sm font-bold ${
                  cond.fishing.index >= 8 ? 'bg-green-500/20 text-green-400' :
                  cond.fishing.index >= 5 ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-red-500/20 text-red-400'
                }`}>
                  {cond.fishing.index}/10 {cond.fishing.rating}
                </div>
              </div>
              <div className="w-full bg-gray-700/30 rounded-full h-2.5 mb-3">
                <div
                  className={`h-2.5 rounded-full ${
                    cond.fishing.index >= 8 ? 'bg-green-400' :
                    cond.fishing.index >= 5 ? 'bg-yellow-400' : 'bg-red-400'
                  }`}
                  style={{ width: `${cond.fishing.index * 10}%` }}
                />
              </div>
              <ul className={`text-xs space-y-1 ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>
                {cond.fishing.factors?.map((f, i) => (
                  <li key={`factor-${f}`} className="flex items-center gap-1">
                    <Activity className="w-3 h-3 text-green-400" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* USGS Water Monitoring Stations */}
          {usgsStations.length > 0 && (
            <section className="mb-8">
              <h2 className={`text-sm font-semibold uppercase tracking-wider mb-4 ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>
                USGS Water Monitoring Stations
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {usgsStations.map((station) => (
                  <div key={station.site_code} className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5 border border-white/5' : 'bg-white border border-gray-200'}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium text-sm">{station.name}</p>
                        <p className={`text-xs ${isDarkMode ? 'text-white/40' : 'text-gray-400'} flex items-center gap-1`}>
                          <Compass className="w-3 h-3" /> {station.distance_mi} mi away
                        </p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'}`}>USGS</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-3">
                      {station.water_temp_f && (
                        <div className={`p-2 rounded-lg text-center ${isDarkMode ? 'bg-cyan-500/10' : 'bg-cyan-50'}`}>
                          <Thermometer className="w-4 h-4 mx-auto text-cyan-400 mb-1" />
                          <p className="text-lg font-bold">{station.water_temp_f}°F</p>
                          <p className={`text-xs ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>Water Temp</p>
                        </div>
                      )}
                      {station.gage_height_ft && (
                        <div className={`p-2 rounded-lg text-center ${isDarkMode ? 'bg-blue-500/10' : 'bg-blue-50'}`}>
                          <Waves className="w-4 h-4 mx-auto text-blue-400 mb-1" />
                          <p className="text-lg font-bold">{station.gage_height_ft} ft</p>
                          <p className={`text-xs ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>Water Level</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <p className={`text-xs mt-2 ${isDarkMode ? 'text-white/30' : 'text-gray-400'}`}>
                Source: {wm?.source || "USGS"} — Real-time provisional data
              </p>
            </section>
          )}
        </div>
      </div>
    );
  }

  const { current_conditions: cc, weather: wx, tides, conditions: cond, hourly_forecast: hourly } = data;
  const displayHours = showAllHours ? hourly : hourly?.slice(0, 12);

  return (
    <div data-testid="marine-forecast-page" className={`min-h-screen pb-12 ${isDarkMode ? 'bg-[#0a0e1a] text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Link to="/" data-testid="marine-back-btn" className={`flex items-center gap-2 text-sm ${isDarkMode ? 'text-white/60 hover:text-white' : 'text-gray-500 hover:text-gray-900'} transition-colors`}>
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>
          <button onClick={fetchData} data-testid="marine-coastal-refresh-button" className={`text-xs px-3 py-1.5 rounded-lg ${isDarkMode ? 'bg-white/10 hover:bg-white/15 text-white/70' : 'bg-gray-200 hover:bg-gray-300 text-gray-600'} transition-colors`}>
            Refresh
          </button>
        </div>

        {/* Title + Advisory */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className={`p-2.5 rounded-xl ${isDarkMode ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
              <Anchor className={`w-6 h-6 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Marine Forecast</h1>
              <p className={`text-sm ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>
                Comprehensive ocean & boating conditions
              </p>
            </div>
          </div>
          {cond.boating.advisory && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-3 flex items-center gap-2 bg-yellow-500/15 text-yellow-400 border border-yellow-500/25 text-sm font-medium px-4 py-2.5 rounded-xl"
            >
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              {cond.boating.advisory}
            </motion.div>
          )}
        </div>

        <MarineSourceOverlay mode="coastal" isDarkMode={isDarkMode} />

        {/* Current Conditions Grid */}
        <section className="mb-8">
          <h2 className={`text-sm font-semibold uppercase tracking-wider mb-4 ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>
            Current Conditions
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            <StatCard icon={Waves} label="Wave Height" value={`${cc.wave_height_ft} ft`} sub={`${cc.wave_conditions} · ${cc.wave_period_sec}s period`} color="text-blue-400" isDarkMode={isDarkMode} />
            <StatCard icon={Activity} label="Swell" value={`${cc.swell_height_ft} ft`} sub={`${cc.swell_direction} · ${cc.swell_period_sec}s`} color="text-indigo-400" isDarkMode={isDarkMode} />
            <StatCard icon={Wind} label="Wind" value={`${wx.wind_speed_mph} mph`} sub={`${wx.wind_direction} ${wx.wind_gust_mph ? `· Gusts ${wx.wind_gust_mph}mph` : ''}`} color="text-slate-400" isDarkMode={isDarkMode} />
            <StatCard icon={Compass} label="Ocean Current" value={`${cc.ocean_current_knots} kt`} sub={cc.ocean_current_direction} color="text-teal-400" isDarkMode={isDarkMode} />
            <StatCard icon={Thermometer} label="Water Temp" value={`${wx.water_temp_f}°F`} sub={`${wx.water_temp_c}°C`} color="text-cyan-400" isDarkMode={isDarkMode} />
            <StatCard icon={Eye} label="Visibility" value={`${wx.visibility_nm} nm`} sub={wx.description} color="text-amber-400" isDarkMode={isDarkMode} />
            <StatCard icon={Gauge} label="Pressure" value={`${wx.pressure_hpa} hPa`} sub={`Humidity ${wx.humidity}%`} color="text-purple-400" isDarkMode={isDarkMode} />
            <StatCard icon={Sun} label="UV Index" value={wx.uv_index} sub={wx.uv_index >= 8 ? "Very High - protect skin" : wx.uv_index >= 6 ? "High" : wx.uv_index >= 3 ? "Moderate" : "Low"} color="text-yellow-400" isDarkMode={isDarkMode} />
          </div>
        </section>

        {/* Beaufort Scale + Wind Wave */}
        <section className="mb-8">
          <div className={`p-5 rounded-2xl ${isDarkMode ? 'bg-white/5 border border-white/5' : 'bg-white border border-gray-200'}`}>
            <div className="flex items-center gap-3 mb-3">
              <Wind className={`w-5 h-5 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`} />
              <div>
                <h3 className="font-semibold">Beaufort Scale {wx.beaufort?.scale}</h3>
                <p className={`text-sm ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>
                  {wx.beaufort?.description} — {wx.beaufort?.sea_state}
                </p>
              </div>
            </div>
            <div className={`h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-white/10' : 'bg-gray-200'}`}>
              <div
                className="h-full rounded-full bg-gradient-to-r from-green-400 via-yellow-400 to-red-500 transition-all"
                style={{ width: `${Math.min((wx.beaufort?.scale || 0) / 12 * 100, 100)}%` }}
              />
            </div>
          </div>
        </section>

        {/* Activity Ratings */}
        <section className="mb-8">
          <h2 className={`text-sm font-semibold uppercase tracking-wider mb-4 ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>
            Activity Conditions
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Boating */}
            <div data-testid="boating-conditions" className={`p-5 rounded-2xl ${isDarkMode ? 'bg-white/5 border border-white/5' : 'bg-white border border-gray-200'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Ship className="w-5 h-5 text-blue-400" />
                  <h3 className="font-semibold">Boating</h3>
                </div>
                <RatingBadge rating={cond.boating.rating} isDarkMode={isDarkMode} />
              </div>
              <div className="flex items-center gap-2 mb-3">
                <span className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{cond.boating.index}</span>
                <span className={`text-sm ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>/10</span>
              </div>
              {cond.boating.warnings?.length > 0 && (
                <div className="space-y-1">
                  {cond.boating.warnings.map((w, i) => (
                    <p key={`bwarn-${w}`} className="text-xs text-yellow-400 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> {w}
                    </p>
                  ))}
                </div>
              )}
            </div>

            {/* Fishing */}
            <div data-testid="fishing-conditions" className={`p-5 rounded-2xl ${isDarkMode ? 'bg-white/5 border border-white/5' : 'bg-white border border-gray-200'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Fish className="w-5 h-5 text-emerald-400" />
                  <h3 className="font-semibold">Fishing</h3>
                </div>
                <RatingBadge rating={cond.fishing.rating} isDarkMode={isDarkMode} />
              </div>
              <div className="flex items-center gap-2 mb-3">
                <span className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{cond.fishing.index}</span>
                <span className={`text-sm ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>/10</span>
              </div>
              {cond.fishing.factors?.length > 0 && (
                <div className="space-y-1">
                  {cond.fishing.factors.map((f, i) => (
                    <p key={`ffact-${f}`} className={`text-xs ${isDarkMode ? 'text-emerald-400/70' : 'text-emerald-600'}`}>{f}</p>
                  ))}
                </div>
              )}
            </div>

            {/* Swimming */}
            <div data-testid="swimming-conditions" className={`p-5 rounded-2xl ${isDarkMode ? 'bg-white/5 border border-white/5' : 'bg-white border border-gray-200'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Waves className="w-5 h-5 text-cyan-400" />
                  <h3 className="font-semibold">Swimming</h3>
                </div>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${
                  cond.swimming.safe
                    ? 'bg-green-500/20 text-green-400 border-green-500/30'
                    : 'bg-red-500/20 text-red-400 border-red-500/30'
                }`}>
                  {cond.swimming.safe ? "Safe" : "Caution"}
                </span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className={isDarkMode ? 'text-white/50' : 'text-gray-500'}>Water Temp</span>
                  <span className="font-medium">{cond.swimming.water_temp_f}°F</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className={isDarkMode ? 'text-white/50' : 'text-gray-500'}>Rip Current Risk</span>
                  <span className={`font-medium ${
                    cond.swimming.rip_current_risk === 'High' ? 'text-red-400' :
                    cond.swimming.rip_current_risk === 'Moderate' ? 'text-yellow-400' : 'text-green-400'
                  }`}>{cond.swimming.rip_current_risk}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Tide Schedule */}
        <section className="mb-8">
          <h2 className={`text-sm font-semibold uppercase tracking-wider mb-4 ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>
            Today's Tides
          </h2>
          <div className={`p-5 rounded-2xl ${isDarkMode ? 'bg-white/5 border border-white/5' : 'bg-white border border-gray-200'}`}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
              {tides.today.map((tide, idx) => (
                <div
                  key={`tide-${tide.type}-${tide.time}`}
                  data-testid={`tide-${tide.type.toLowerCase()}-${idx}`}
                  className={`p-4 rounded-xl text-center ${
                    tide.type === 'High'
                      ? isDarkMode ? 'bg-blue-500/15 border border-blue-500/20' : 'bg-blue-50 border border-blue-200'
                      : isDarkMode ? 'bg-cyan-500/10 border border-cyan-500/15' : 'bg-cyan-50 border border-cyan-200'
                  }`}
                >
                  <p className={`text-xs font-semibold mb-1 ${tide.type === 'High' ? 'text-blue-400' : 'text-cyan-400'}`}>
                    {tide.type} Tide
                  </p>
                  <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{tide.height_ft} ft</p>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>{tide.time}</p>
                </div>
              ))}
            </div>
            <p className={`text-xs text-center ${isDarkMode ? 'text-white/30' : 'text-gray-400'}`}>
              Tidal Range: {tides.tidal_range_ft} ft · Type: {tides.tide_type}
            </p>
          </div>
        </section>

        {/* Hourly Wave Forecast */}
        <section className="mb-8">
          <h2 className={`text-sm font-semibold uppercase tracking-wider mb-4 ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>
            Hourly Wave Forecast
          </h2>
          <div className={`rounded-2xl overflow-hidden ${isDarkMode ? 'bg-white/5 border border-white/5' : 'bg-white border border-gray-200'}`}>
            {/* Header */}
            <div className={`grid grid-cols-5 text-xs font-semibold py-3 px-3 ${isDarkMode ? 'bg-white/5 text-white/50' : 'bg-gray-100 text-gray-500'}`}>
              <span>Time</span>
              <span className="text-center">Waves</span>
              <span className="text-center">Swell</span>
              <span className="text-center">Direction</span>
              <span className="text-center">Current</span>
            </div>
            {/* Rows */}
            <div className={`divide-y ${isDarkMode ? 'divide-white/5' : 'divide-gray-100'}`}>
              {displayHours?.map((entry, idx) => (
                <HourlyForecastRow key={entry.time || `hour-${idx}`} entry={entry} isDarkMode={isDarkMode} />
              ))}
            </div>
            {/* Show more */}
            {hourly?.length > 12 && (
              <button
                data-testid="show-more-hours-btn"
                onClick={() => setShowAllHours(!showAllHours)}
                className={`w-full py-3 text-xs font-medium flex items-center justify-center gap-1 ${isDarkMode ? 'text-blue-400 hover:bg-white/5' : 'text-blue-600 hover:bg-gray-50'} transition-colors`}
              >
                {showAllHours ? "Show Less" : `Show All ${hourly.length} Hours`}
                <ChevronRight className={`w-3 h-3 transition-transform ${showAllHours ? 'rotate-90' : ''}`} />
              </button>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
